import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { applyEmailVerificationCode } from '../../services/auth';
import { clearActionCodeFromAddressBar } from '../../utils/authActionUrl';
import { InviteShell } from './AcceptInviteLayout';
import { ResetPassword } from './ResetPassword';
import { SignupHeader } from '../signup/SignupLayout';

/**
 * Where the verification screens send the coach next. A constant, never the link's `continueUrl`:
 * that is attacker-editable query string, and signup is the only flow that sends these emails.
 */
const NEXT_PATH = '/signup';

/**
 * Firebase's custom email action handler, at `/auth/complete-reset` and `/reset-password`.
 *
 * The Firebase project points every email template at one URL (the name predates email
 * verification), and which of the two it is may differ per environment, so both switch on `mode`:
 * - `resetPassword` is the existing reset flow, unchanged. `ResetPassword` reads the same `oobCode`.
 * - `verifyEmail` applies the code and says so. It does **not** sign anyone out: the coach is usually
 *   mid-signup in another tab of this browser, and that session is what continues.
 * - anything else (`recoverEmail`, a mode added later, none at all) gets a neutral message rather
 *   than an attempt to act on a code this page does not understand. At `/reset-password`
 *   (`resetByDefault`) it is the reset flow instead, as that route has always been.
 *
 * The code is dropped from the address bar once read (see `clearActionCodeFromAddressBar`).
 */
export const AuthActionPage = ({ resetByDefault = false }: { resetByDefault?: boolean }) => {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');

  useEffect(() => {
    clearActionCodeFromAddressBar();
  }, []);

  if (mode === 'verifyEmail') {
    // Keyed by the code, so a new link opened into this same page applies its own code.
    const oobCode = searchParams.get('oobCode');
    return <VerifyEmailAction key={oobCode ?? ''} oobCode={oobCode} />;
  }

  if (mode === 'resetPassword' || resetByDefault) {
    return <ResetPassword />;
  }

  return <UnsupportedAction />;
};

type VerifyState = 'verifying' | 'verified' | 'invalid' | 'failed';

const verifyFailureState = (error: unknown): VerifyState => {
  const code =
    typeof error === 'object' && error !== null && 'code' in error
      ? String((error as { code: unknown }).code)
      : '';
  return code === 'auth/expired-action-code' ||
    code === 'auth/invalid-action-code' ||
    code === 'auth/user-not-found' ||
    code === 'auth/user-disabled'
    ? 'invalid'
    : 'failed';
};

const VerifyEmailAction = ({ oobCode }: { oobCode: string | null }) => {
  const [state, setState] = useState<VerifyState>(oobCode ? 'verifying' : 'invalid');
  const startedRef = useRef(false);

  useEffect(() => {
    // Once: an action code is single-use, so a second apply (StrictMode's double effect) would fail
    // and overwrite the success with "link already used".
    if (!oobCode || startedRef.current) return;
    startedRef.current = true;

    applyEmailVerificationCode(oobCode).then(
      () => setState('verified'),
      (error: unknown) => setState(verifyFailureState(error))
    );
  }, [oobCode]);

  if (state === 'verifying') {
    return (
      <InviteShell>
        <div className="accept-invite-loading" role="status" data-testid="auth-action-verifying">
          <p>Verifying your email...</p>
        </div>
      </InviteShell>
    );
  }

  if (state === 'verified') {
    return (
      <InviteShell>
        <SignupHeader title="Your email is verified" />
        <div
          className="accept-invite-message accept-invite-message--success"
          data-testid="auth-action-verified"
        >
          <p>
            Go back to your signup and choose I&apos;ve verified to continue. If it is still open
            in another tab, you can continue there.
          </p>
        </div>
        <div className="accept-invite-actions">
          <Link
            to={NEXT_PATH}
            className="btn btn--primary btn--full-width signup-link-button"
            data-testid="auth-action-continue"
          >
            Continue signup
          </Link>
        </div>
      </InviteShell>
    );
  }

  return (
    <InviteShell>
      <SignupHeader title="We could not verify your email" />
      <div
        className="accept-invite-message accept-invite-message--error"
        role="alert"
        data-testid="auth-action-verify-error"
      >
        <p>
          {state === 'invalid'
            ? 'This verification link has expired or has already been used.'
            : 'Something went wrong verifying your email. Please try the link again.'}
        </p>
        <p>
          If you already verified, go back to your signup and continue. Otherwise, use Resend email
          there to get a new link.
        </p>
      </div>
      <div className="accept-invite-actions">
        <Link to={NEXT_PATH} className="accept-invite-link" data-testid="auth-action-back">
          Back to signup
        </Link>
      </div>
    </InviteShell>
  );
};

const UnsupportedAction = () => (
  <InviteShell>
    <SignupHeader title="This link can't be used here" />
    <div
      className="accept-invite-message accept-invite-message--warning"
      data-testid="auth-action-unsupported"
    >
      <p>
        This email link isn&apos;t one RosterPilot can complete. If you didn&apos;t expect this
        email, you can ignore it. For help with your account, contact your program&apos;s admin.
      </p>
    </div>
    <div className="accept-invite-actions">
      <Link to="/login" className="accept-invite-link">
        Go to sign in
      </Link>
    </div>
  </InviteShell>
);
